import { Avatar, AvatarSizes, Box, Stack } from "@skailer-com/uikit";
import { ReactElement, ReactNode } from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

interface UserCardThemeType {
  cover: string;
  title: string;
  caption: string;
  extra: string;
  ellipsis: string;
  variants: {
    sizes: {
      cover: {
        [key in keyof UserCardSizes]?: string;
      };
      title: {
        [key in keyof UserCardSizes]?: string;
      };
    };
  };
}

const userCardTheme: UserCardThemeType = {
  cover: "",
  title: "text-dark/95 font-semibold text-lg px-2",
  caption: "text-base text-dark/60 px-2 whitespace-pre-wrap",
  extra: "text-base text-dark/60 px-2",
  ellipsis: "text-ellipsis overflow-hidden whitespace-nowrap w-40",
  variants: {
    sizes: {
      cover: {
        sm: "text-center items-center",
        md: "w-40",
      },
      title: {
        sm: "text-base leading-5",
        md: "leading-6",
      },
    },
  },
};

export interface UserCardProps {
  title: string;
  titleSlot?: ReactNode;
  caption?: string;
  captionSlot?: ReactNode;
  extra?: string;
  avatarElement?: ReactElement;
  avatarUrl?: string;
  textEllipsis?: boolean;
  className?: string;
  size?: keyof UserCardSizes;
  onClick?: () => void;
  to?: string;
  target?: "_blank" | "_self";
}

export interface UserCardSizes {
  sm: string;
  md: string;
}

export function UserCard({
  title,
  titleSlot,
  caption,
  captionSlot,
  extra,
  avatarElement,
  avatarUrl,
  textEllipsis = false,
  size = "md",
  onClick,
  to,
  target = "_self",
  className,
}: UserCardProps) {
  const avatarMapping: Record<keyof UserCardSizes, keyof AvatarSizes> = {
    sm: "xl",
    md: "2xl",
  };

  const isClickable = Boolean(onClick || to);

  const handleClick = (e: React.MouseEvent) => {
    if (to && !e.metaKey && !e.ctrlKey && onClick) {
      e.preventDefault();
      onClick();
    } else if (!to && onClick) {
      onClick();
    }
  };

  const content = (
    <Stack gap={"sm"} direction={"vertical"} items={"center"}>
      {avatarElement ? (
        avatarElement
      ) : (
        <Avatar
          url={avatarUrl}
          size={avatarMapping[size]}
          acronym={title}
          className={"self-center"}
        />
      )}
      <Box className="self-stretch min-w-0">
        <div
          className={twMerge(
            "user-title",
            textEllipsis && userCardTheme.ellipsis,
            userCardTheme.title,
            userCardTheme.variants.sizes.title[size]
          )}
        >
          {title}
        </div>
        {titleSlot && titleSlot}
        {caption && (
          <div className={twMerge(userCardTheme.caption, textEllipsis && userCardTheme.ellipsis)}>
            {caption}
          </div>
        )}
        {captionSlot && captionSlot}
        {extra && (
          <div className={twMerge(userCardTheme.extra, textEllipsis && userCardTheme.ellipsis)}>
            {extra}
          </div>
        )}
      </Box>
    </Stack>
  );

  if (to) {
    return (
      <Link
        to={to}
        onClick={handleClick}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : undefined}
        className={twMerge(
          userCardTheme.variants.sizes.cover[size],
          className,
          isClickable && "cursor-pointer group no-underline"
        )}
      >
        {content}
      </Link>
    );
  }

  if (onClick) {
    return (
      <div
        onClick={handleClick}
        className={twMerge(
          userCardTheme.variants.sizes.cover[size],
          className,
          isClickable && "cursor-pointer group"
        )}
      >
        {content}
      </div>
    );
  }

  return (
    <div className={twMerge(userCardTheme.variants.sizes.cover[size], className)}>{content}</div>
  );
}
